import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ChevronLeft, Lock, Eye, Database, Users, Mail, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Information We Collect",
      icon: Database,
      content: [
        "Personal information you provide (name, email, phone number, business information)",
        "Usage data and analytics (pages visited, features used, time spent)",
        "Device and browser information",
        "Location data (with your consent)",
        "Communications and correspondence",
        "Payment information (processed securely through third-party providers)"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: Eye,
      content: [
        "To provide and improve our directory services",
        "To facilitate AI-powered chat and assistance features",
        "To process business listings and submissions",
        "To send relevant notifications and updates",
        "To analyze and improve user experience",
        "To prevent fraud and ensure platform security"
      ]
    },
    {
      title: "AI Technology Usage",
      icon: Globe,
      content: [
        "Our platform uses AI technology to enhance user experience",
        "AI chatbots provide automated assistance and recommendations",
        "AI helps match users with relevant businesses",
        "All AI interactions are clearly labeled",
        "You can request human assistance at any time",
        "AI-generated content is reviewed for accuracy"
      ]
    },
    {
      title: "Data Protection",
      icon: Lock,
      content: [
        "Enterprise-grade encryption for all data transmission",
        "Secure cloud storage with regular backups",
        "Strict access controls and authentication",
        "Regular security audits and updates",
        "Compliance with industry security standards",
        "Data breach notification procedures"
      ]
    },
    {
      title: "Your Rights",
      icon: Users,
      content: [
        "Access your personal data upon request",
        "Request correction of inaccurate information",
        "Request deletion of your data (right to be forgotten)",
        "Opt-out of marketing communications",
        "Data portability - receive your data in a usable format",
        "Withdraw consent for data processing"
      ]
    },
    {
      title: "Cookies & Tracking",
      icon: Shield,
      content: [
        "We use cookies to improve site functionality",
        "Analytics cookies help us understand usage patterns",
        "You can control cookie preferences in your browser",
        "Third-party services may set their own cookies",
        "We honor Do Not Track browser settings",
        "Essential cookies are required for core functionality"
      ]
    }
  ];

  return (
    <div className="page-container min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/20 via-transparent to-[#00E5FF]/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Directory
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">Privacy Policy</h1>
                <p className="text-[#94A3B8]">Last updated: February 6, 2026</p>
              </div>
            </div>
            
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              LocalBest.ai ("we," "us," or "our") is committed to protecting 
              your privacy. This policy explains how we collect, use, and safeguard your information.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-8">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">{section.title}</h2>
                </div>
                <ul className="space-y-2">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#94A3B8]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card rounded-xl p-6 border-2 border-[#7C3AED]/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#7C3AED]" />
              </div>
              <h2 className="text-xl font-bold text-white">Contact Us</h2>
            </div>
            <p className="text-[#94A3B8] mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-2 text-[#94A3B8]">
              <p><strong className="text-white">Email:</strong> privacy@localbest.ai</p>
              <p><strong className="text-white">Website:</strong> https://localbest.ai</p>
            </div>
            <div className="mt-6">
              <a href="mailto:privacy@localbest.ai">
                <Button className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Privacy Team
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
